import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  assertNever,
  FieldAgreement,
  MatchVerdictDecision,
  type PossibleMatchDto,
  type PossibleMatchPersonDto,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useMatchConfirmPreview, usePossibleMatches, useRecordMatchVerdict } from '@/hooks/queries';
import { candidateKey, firstVerdict, largestWaitingGift, titleCase, titleCaseName } from '@/lib/fundraising/matchVerdict';
import { cn } from '@/lib/utils';
import { FundraisingPage } from './FundraisingPage';
import { layerOpen, typing } from './useSelectionKeys';

const dollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const count = new Intl.NumberFormat('en-US');

const joined = (separator: string, ...parts: (string | false | null | undefined)[]) =>
  parts.filter((part): part is string => !!part).join(separator);

// Each record's letter, and its key. N and S are taken by None of them and Skip.
const LETTERS = 'ABCDEFGHIJKLMOPQRTUVWXYZ';



type Answered = { row: PossibleMatchPersonDto; tag: 'Confirmed' | 'Not them' };

/** Enter on a focused control presses that control; it isn't the Confirm shortcut. */
const pressesControl = (target: EventTarget | null) => target instanceof HTMLElement && ['BUTTON', 'A'].includes(target.tagName);

/**
 * The Match review tab: the Review Queue, one person at a time, largest candidate gift first — ordered by
 * the size of the error a wrong answer causes. Your list's record is pinned on top with each possible
 * federal record under it, each field labelled Same / Close / Differs as the server compared it; mark any
 * number as "them" and see the giving they bring and what the server says the Ask becomes, then confirm,
 * turn them all down, or skip. Keys: a record's letter marks it, Enter confirms, N is None of them, S
 * skips. Several answers go as several calls to the verdict PUT, one per record. A prospect page sends a
 * person here with `initialProspectId`, which opens them first; their name opens their page.
 */
export function MatchReview({ initialProspectId }: { initialProspectId?: string } = {}) {
  const { data: review, isLoading, isError } = usePossibleMatches();
  const { mutate: decide, error: decideError, reset } = useRecordMatchVerdict();
  const [answered, setAnswered] = useState<Answered[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string | null>(initialProspectId ?? null);
  const [them, setThem] = useState<Set<string>>(() => new Set());

  const answeredIds = useMemo(() => new Set(answered.map((a) => a.row.prospect.id)), [answered]);
  const waiting = useMemo(
    () =>
      (review?.people ?? [])
        .filter((row) => row.possibleMatches.length > 0 && !answeredIds.has(row.prospect.id))
        .sort((a, b) => largestWaitingGift(b) - largestWaitingGift(a) || a.prospect.name.localeCompare(b.prospect.name)),
    [review?.people, answeredIds],
  );
  // Skipped people go to the back of the line, in the order they were skipped.
  const ordered = useMemo(() => {
    const later = new Set(skipped);
    return [...waiting.filter((row) => !later.has(row.prospect.id)), ...skipped.map((id) => waiting.find((row) => row.prospect.id === id)).filter((row): row is PossibleMatchPersonDto => !!row)];
  }, [waiting, skipped]);
  const active = ordered.find((row) => row.prospect.id === activeId) ?? ordered[0];

  const open = (id: string) => {
    if (id !== activeId) reset();
    setThem(new Set());
    setActiveId(id);
  };
  const advance = (from: PossibleMatchPersonDto) => {
    const rest = ordered.filter((row) => row.prospect.id !== from.prospect.id);
    setThem(new Set());
    setActiveId(rest[0]?.prospect.id ?? null);
  };

  const answer = (row: PossibleMatchPersonDto, accepted: Set<string>) => {
    reset();
    // A refused answer puts the person back in front, so the error shows beside the person it's about.
    const refused = () => {
      setAnswered((was) => was.filter((a) => a.row.prospect.id !== row.prospect.id));
      setThem(new Set());
      setActiveId(row.prospect.id);
    };
    for (const candidate of row.possibleMatches) {
      const decision = accepted.has(candidateKey(candidate)) ? MatchVerdictDecision.Accept : MatchVerdictDecision.Reject;
      decide({ prospectId: row.prospect.id, request: firstVerdict(candidate, decision) }, { onError: refused });
    }
    setAnswered((was) => [{ row, tag: accepted.size > 0 ? 'Confirmed' : 'Not them' }, ...was]);
    setSkipped((was) => was.filter((id) => id !== row.prospect.id));
    advance(row);
  };
  const skip = (row: PossibleMatchPersonDto) => {
    setSkipped((was) => [...was.filter((id) => id !== row.prospect.id), row.prospect.id]);
    advance(row);
  };
  const toggleThem = (candidate: PossibleMatchDto) =>
    setThem((was) => {
      const next = new Set(was);
      const key = candidateKey(candidate);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const keys = useRef({ active, them, answer, skip, toggleThem });
  keys.current = { active, them, answer, skip, toggleThem };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const { active: row, them: marked, answer: confirm, skip: pass, toggleThem: mark } = keys.current;
      if (!row || e.metaKey || e.ctrlKey || e.altKey || typing(e.target) || layerOpen()) return;
      const key = e.key.toUpperCase();
      if (e.key === 'Enter') {
        // Enter confirms what's marked; with nothing marked it does nothing, so a stray Enter never turns
        // every record down.
        if (pressesControl(e.target) || marked.size === 0) return;
        e.preventDefault();
        confirm(row, marked);
      } else if (key === 'N') {
        e.preventDefault();
        confirm(row, new Set());
      } else if (key === 'S') {
        e.preventDefault();
        pass(row);
      } else if (key.length === 1 && LETTERS.includes(key)) {
        const candidate = row.possibleMatches[LETTERS.indexOf(key)];
        if (candidate) {
          e.preventDefault();
          mark(candidate);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <FundraisingPage tab="match-review">
      {isLoading && <Skeleton className="h-64 w-full" />}
      {isError && !isLoading && <p className="text-sm text-muted-foreground">We couldn’t load the possible matches right now.</p>}
      {review && ordered.length === 0 && answered.length === 0 && (
        <p className="mx-auto max-w-xl rounded-xl border bg-card px-5 py-10 text-center text-[13px] text-muted-foreground">
          No possible matches to review. New ones appear only when matching finds a federal record that might be someone
          in your book.
        </p>
      )}
      {review && (ordered.length > 0 || answered.length > 0) && (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="overflow-hidden rounded-xl border bg-card">
            <div className="border-b px-4 py-3">
              <p className="text-[13px] font-semibold">
                {count.format(ordered.length)} possible {ordered.length === 1 ? 'match' : 'matches'} left
              </p>
              <p className="text-xs text-muted-foreground">Largest federal gift first</p>
            </div>
            <ul aria-label="Possible matches left" className="max-h-[60vh] overflow-y-auto">
              {answered.map(({ row, tag }) => (
                <li key={row.prospect.id} className="flex min-h-14 items-center gap-3 border-b px-4 py-2 text-muted-foreground">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold">{row.prospect.name}</span>
                    <span className="block truncate text-xs">{joined(' · ', row.prospect.employer, row.prospect.city)}</span>
                  </span>
                  <span className={cn('text-xs font-semibold', tag === 'Confirmed' && 'text-success')}>{tag}</span>
                </li>
              ))}
              {ordered.map((row) => {
                const isActive = row === active;
                return (
                  <li key={row.prospect.id} className="border-b last:border-0">
                    <button
                      type="button"
                      aria-current={isActive ? 'true' : undefined}
                      onClick={() => open(row.prospect.id)}
                      className={cn(
                        'flex min-h-14 w-full items-center gap-3 px-4 py-2 text-left hover:bg-muted/40',
                        isActive && 'bg-primary/5 shadow-[inset_3px_0_0_var(--color-primary)]',
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-semibold text-foreground">{row.prospect.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {joined(' · ', row.prospect.employer, joined(', ', row.prospect.city, row.prospect.state))}
                        </span>
                      </span>
                      <span className="text-xs tabular-nums text-muted-foreground">{dollars.format(largestWaitingGift(row))}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {active ? (
            <Candidates
              key={active.prospect.id}
              row={active}
              them={them}
              onToggle={toggleThem}
              onConfirm={() => answer(active, them)}
              onNone={() => answer(active, new Set())}
              onSkip={() => skip(active)}
              error={decideError?.message ?? null}
              reviewed={answered.length}
              ofTotal={answered.length + ordered.length}
            />
          ) : (
            <p className="rounded-xl border bg-card px-5 py-10 text-center text-[13px] text-muted-foreground">
              That’s everyone for now.
            </p>
          )}
        </div>
      )}
    </FundraisingPage>
  );
}

const plural = (n: number, one: string, many = `${one}s`) => `${count.format(n)} ${n === 1 ? one : many}`;

/** "Same" in the settled colour, "Differs" in the needs-you colour, "Close" quiet — always with its word. */
function Agreement({ label }: { label: FieldAgreement | null }) {
  if (!label) return null;
  const tone = (() => {
    switch (label) {
      case FieldAgreement.Same:
        return { text: 'text-success', dot: 'bg-success' };
      case FieldAgreement.Differs:
        return { text: 'text-warning', dot: 'bg-warning' };
      case FieldAgreement.Close:
        return { text: 'text-foreground', dot: 'bg-muted-foreground' };
      default:
        return assertNever(label);
    }
  })();
  return (
    <span data-agreement className={cn('mt-0.5 flex items-center gap-1.5 text-[11px] font-semibold', tone.text)}>
      <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', tone.dot)} />
      {label}
    </span>
  );
}

/** One field of a record: its value, and how it compares with your list's. */
function Field({ value, label, strong }: { value: string; label: FieldAgreement | null; strong?: boolean }) {
  return (
    <span role="cell" className="min-w-0">
      <span className={cn('block truncate', strong || label === FieldAgreement.Differs ? 'font-semibold' : undefined)}>{value || '—'}</span>
      <Agreement label={label} />
    </span>
  );
}

const listedAs = (letters: string[]) =>
  letters.length > 1 ? `${letters.slice(0, -1).join(', ')} and ${letters[letters.length - 1]}` : letters[0];

function Candidates({
  row,
  them,
  onToggle,
  onConfirm,
  onNone,
  onSkip,
  error,
  reviewed,
  ofTotal,
}: {
  row: PossibleMatchPersonDto;
  them: Set<string>;
  onToggle: (candidate: PossibleMatchDto) => void;
  onConfirm: () => void;
  onNone: () => void;
  onSkip: () => void;
  error: string | null;
  reviewed: number;
  ofTotal: number;
}) {
  const { prospect, possibleMatches } = row;
  const markedRecords = possibleMatches.flatMap((c, i) => (them.has(candidateKey(c)) ? [{ candidate: c, letter: LETTERS[i] }] : []));
  const marked = markedRecords.map((r) => r.letter);
  const markedLabel = listedAs(marked);
  // What confirming them would do, worked out on the server by the rule the book sizes the Ask with.
  const { data: preview } = useMatchConfirmPreview(
    prospect.id,
    markedRecords.map((r) => r.candidate),
  );
  const cols =
    'grid grid-cols-[72px_minmax(0,1.2fr)_minmax(0,1.4fr)_minmax(0,1.3fr)_minmax(0,1fr)_110px_140px] items-center gap-3';

  return (
    <section aria-labelledby="which-are-them" className="space-y-4 rounded-xl border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 id="which-are-them" className="text-base font-semibold">
            Which of these are them?
          </h2>
          <p className="text-[13px] text-muted-foreground">
            <Link to="/admin/fundraising/prospects/$prospectId" params={{ prospectId: prospect.id }} className="hover:underline">
              {prospect.name}
            </Link>{' '}
            · {plural(possibleMatches.length, 'possible federal record')}
          </p>
        </div>
        <p className="text-xs tabular-nums text-muted-foreground">
          {count.format(reviewed)} of {count.format(ofTotal)} reviewed
        </p>
      </div>
      <div role="table" aria-label={`${prospect.name}’s records`} className="overflow-x-auto text-[13px]">
        <div role="row" className={cn(cols, 'min-w-[760px] pb-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground')}>
          <span role="columnheader" />
          <span role="columnheader">Name</span>
          <span role="columnheader">Address</span>
          <span role="columnheader">Employer</span>
          <span role="columnheader">Occupation</span>
          <span role="columnheader" className="text-right">
            Federal gifts
          </span>
          <span role="columnheader">Is this them?</span>
        </div>
        <div role="row" aria-label="Your list" className={cn(cols, 'min-h-14 min-w-[760px] rounded-lg bg-muted py-2')}>
          <span role="cell" className="pl-3 text-xs font-semibold text-muted-foreground">
            Your list
          </span>
          <Field value={prospect.name} label={null} strong />
          <Field value={joined(', ', prospect.address, prospect.city, joined(' ', prospect.state, prospect.zipCode))} label={null} />
          <Field value={prospect.employer ?? ''} label={null} />
          <Field value={prospect.occupation ?? ''} label={null} />
          <span role="cell" className="text-right text-muted-foreground">
            —
          </span>
          <span role="cell" />
        </div>
        {possibleMatches.map((candidate, i) => {
          const isThem = them.has(candidateKey(candidate));
          const { agreement } = candidate;
          return (
            <div
              key={candidateKey(candidate)}
              role="row"
              aria-label={`Record ${LETTERS[i]}`}
              className={cn(cols, 'min-h-[72px] min-w-[760px] border-t py-2', isThem && 'bg-primary/5')}
            >
              <span role="cell" className="pl-3 text-xs font-semibold">
                Record {LETTERS[i]}
              </span>
              <Field value={titleCaseName(joined(' ', candidate.givenName, candidate.middle, candidate.lastCore, candidate.suffix))} label={agreement.name} />
              <Field
                value={joined(', ', titleCase(candidate.streetFolded), titleCase(candidate.city), joined(' ', candidate.state, candidate.zip5))}
                label={agreement.address}
              />
              <Field value={titleCase(candidate.employer)} label={agreement.employer} />
              <Field value={titleCase(candidate.occupation)} label={agreement.occupation} />
              <span role="cell" className="flex flex-col items-end gap-0.5 tabular-nums">
                <span>{dollars.format(candidate.totalFederal)}</span>
                <span className="text-[11px] text-muted-foreground">
                  {candidate.giftCount === 1 ? '1 gift' : `${plural(candidate.giftCount, 'gift')} · largest ${dollars.format(candidate.largestGift)}`}
                </span>
              </span>
              <span role="cell" className="flex gap-0.5 rounded-lg bg-muted p-[3px]">
                <button
                  type="button"
                  aria-pressed={isThem}
                  onClick={() => !isThem && onToggle(candidate)}
                  className={cn('h-9 flex-1 rounded-md text-xs font-semibold', isThem ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground')}
                >
                  Them
                </button>
                <button
                  type="button"
                  aria-pressed={!isThem}
                  onClick={() => isThem && onToggle(candidate)}
                  className={cn('h-9 flex-1 rounded-md text-xs font-semibold', !isThem ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground')}
                >
                  Not them
                </button>
              </span>
            </div>
          );
        })}
      </div>

      {marked.length > 0 && (
        <section aria-labelledby="giving-if-confirmed" className="overflow-hidden rounded-lg border">
          <div className="flex flex-wrap items-center justify-between gap-2 bg-muted/40 px-4 py-2.5">
            <h3 id="giving-if-confirmed" className="text-[13px] font-semibold">
              Giving if you confirm {markedLabel}
            </h3>
            {preview && (
              <span className="text-xs tabular-nums text-muted-foreground">
                {plural(preview.giftCount, 'gift')} · {dollars.format(preview.totalFederal)} federal
              </span>
            )}
          </div>
          <ul className="text-[13px]">
            {markedRecords.flatMap(({ candidate, letter }) =>
              candidate.giving.map((g) => (
                <li
                  key={`${letter}-${g.committee}-${g.cycle}`}
                  className="grid h-10 grid-cols-[80px_minmax(0,1fr)_110px] items-center gap-4 border-t px-4"
                >
                  <span className="text-xs font-semibold text-muted-foreground">Record {letter}</span>
                  <span className="truncate">
                    {g.committee} · {g.cycle} · {plural(g.giftCount, 'gift')}
                    {g.giftCount > 1 && ` · largest ${dollars.format(g.largest)}`}
                  </span>
                  <span className="text-right tabular-nums">{dollars.format(g.total)}</span>
                </li>
              )),
            )}
          </ul>
        </section>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex flex-wrap items-center gap-2">
        {marked.length > 0 ? (
          <Button size="lg" onClick={onConfirm}>
            Confirm {markedLabel}
            <kbd aria-hidden="true" className="ml-2 font-sans text-xs font-medium opacity-70">
              · Enter
            </kbd>
          </Button>
        ) : (
          <Button size="lg" onClick={onNone}>
            None of them
            <kbd aria-hidden="true" className="ml-2 font-sans text-xs font-medium opacity-70">
              · N
            </kbd>
          </Button>
        )}
        {marked.length > 0 && (
          <Button size="lg" variant="outline" onClick={onNone}>
            None of them
            <kbd aria-hidden="true" className="ml-2 font-sans text-xs font-medium text-muted-foreground">
              · N
            </kbd>
          </Button>
        )}
        <Button size="lg" variant="ghost" onClick={onSkip}>
          Skip
          <kbd aria-hidden="true" className="ml-2 font-sans text-xs font-medium text-muted-foreground">
            · S
          </kbd>
        </Button>
        {marked.length > 0 && preview?.ask != null && (
          <span className="text-[13px] text-muted-foreground">
            Suggested ask becomes <span className="font-semibold tabular-nums text-foreground">{dollars.format(preview.ask)}</span>.
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        A confirmed record’s giving counts toward their suggested ask. A record you turn down isn’t suggested for them again.
      </p>
    </section>
  );
}
